/**
 * **What one tile of the COMBAT.app board actually is** — the hovered-tile pane's contents, as pure
 * data. {@link describeCell} is the fold; `TileInfoBody.tsx` is the surface that draws it.
 *
 * The developer's own filing of `terrain-legibility` asked for three things, and this is the second:
 * *"a hovered-tile info pane — not tooltip by the mouse, maybe like a little info pane that tells you
 * everything about the hovered tile."* The legend answers "what does this mark mean"; this answers
 * "what is THIS square", which is the question a GM mid-encounter actually has.
 *
 * ## The tile comes first (`terrain-tile-types` Phase 5, `decisions/0184`)
 *
 * The developer's ruling for named tiles: *"when you hover it, it says 'thick brush' and enumerates
 * both of those properties."* So a painted cell leads with {@link CellDescription.tile}: the tile's
 * name ("Custom terrain" for a one-off cell), then one plain-words sentence per property, from
 * `terrainTiles.ts` — the same module the palette's chips come from. Open ground has no tile reading
 * and keeps the four channels alone. The four channels below it stay as they were: they answer
 * each question in full (the flyer clause, the hazard's armor rule) where the tile lines are the
 * one-glance summary.
 *
 * ## Two registers, and why they are separate types here rather than separate paragraphs
 *
 * A cell carries two different kinds of truth and conflating them is the failure mode this pane
 * exists to avoid (`terrain-legibility` D3/D4/D5):
 *
 *  - **Authored truth** ({@link CellDescription.channels} + {@link CellDescription.carriers}) — what
 *    is painted here, true no matter who is looking. Always present.
 *  - **Resolved truth** ({@link CellDescription.resolved}) — what this cell does to *one specific
 *    shot*, and only ever present while an attack is armed. **Cover is not a property of a cell**:
 *    `terrainVisibility` folds all four carriers against a specific attacker *and* a specific weapon,
 *    and two printed rules make the two registers genuinely diverge — the occupancy floor (a target
 *    standing on a hard-cover cell gets soft, never hard) and hard cover's own adjacency clause (Core
 *    p.66). A pane reporting only authored truth would be *correct and misleading*, which is the
 *    worst combination for a legibility fix.
 *
 * They stay separate all the way down: two fields here, two regions in the DOM, and the authored
 * region's shape does not depend on `resolved` at all — mode-dependent content that rewrites in place
 * reads as flicker, where content that arrives in its own region reads as additive (D5).
 *
 * ## The authored register reports FLAGS, not the render class
 *
 * This is the one place the pane deliberately parts company with `terrainLegend.ts`. The legend names
 * what the board *draws*, so it folds through the renderer's own `cellChannels`. The pane names what
 * the cell *is*, so it reads the flags directly — the job D11 gives it, *"if you can hover to know,
 * the tiles only need to support scanning, not identification"*.
 *
 * That split used to make the two surfaces genuinely disagree: `cellTreatment` was mutually exclusive,
 * so a cell that was both soft cover and difficult was painted as cover alone (65.6% of difficult
 * terrain on a generated map) while the pane reported both. **Phase 3a's channel split closed that**,
 * and `terrainCellDescription.test.ts` now pins the two as *agreeing* on such a cell. Reading flags
 * rather than the render classes is still the right shape, because the pane answers questions the
 * board deliberately does not draw at all — which hazard damage type, and what a move cost actually
 * IS rather than merely that one exists (D10).
 *
 * The four {@link TerrainChannel}s are D1's channel allocation, stated as questions rather than as
 * fields: *can I stand there and what does it cost* / *does it protect whoever shelters there* / *can
 * I see and shoot through it* / *will it hurt me*. They are **always all four, always in that order**
 * — a fixed shape the reader learns once, so "Cover: None" is a real answer rather than a missing row.
 *
 * ## Wording
 *
 * Never raw flag names (D3) — every line is phrased as the mechanical consequence, in the register
 * `objectProfileSummary` and the TERRAIN rail's `title=` strings already speak. The cloud's line is
 * `terrainLegend.ts`'s own ({@link LEGEND_COPY}), so the key and the pane describe it in one
 * vocabulary; the cover lines are {@link COVER_DETAIL}, which the legend's cover rows carried until
 * the key moved to tiles (`terrain-tile-art` 5.3).
 *
 * ## What it deliberately does not answer
 *
 * - **Who is standing here.** A mech on the tile is a token with its own inspector; this pane is
 *   about the ground. Objects and deployables ARE reported — they are the third obstacle carrier, and
 *   what they contribute to sight/cover/movement is terrain in everything but name.
 * - **Whether the armed weapon can reach this cell.** Range is the reach wash's job on the board; the
 *   resolved register answers the LoS/cover question for the cell under the cursor either way.
 * - **Anything attacker-relative with no attack armed** (D6). There would be no attacker to be
 *   relative to, and picking one would be a lie.
 *
 * @module
 */
import {
  CUSTOM_TILE,
  DEFAULT_HAZARD_TYPE,
  contributesToTerrain,
  edgeTerrain,
  hasStatus,
  isOpenDoor,
  propertiesAtKey,
  squareCell,
  worldFor,
  type AreaGrant,
  type TerrainProperties,
  type Combatant,
  type EdgeTerrain,
  type EncounterState,
  type SquareCell,
  type WeaponProfile,
} from "@lancer-vtt/domain";
import { isPhysical, type LoadedOverlay } from "@lancer-vtt/rules";
import type { Cover } from "@lancer-vtt/space";
import { armedCellSightlines, losExemptionFor } from "../../lib/encounter/combatInspector";
import { LEGEND_COPY } from "./terrainLegend";
import { cellTileName, tilePropertyList, type TileProperty } from "./terrainTiles";

/**
 * The four questions a GM asks of a square, in the order the pane answers them — `terrain-legibility`
 * D1's channel allocation. One channel per *question*, never one per flag: that is what makes the
 * vocabulary learnable once instead of memorized per symbol, and it is the same split Phase 3a gives
 * the renderer.
 */
export type TerrainChannel = "floor" | "cover" | "sight" | "hazard";

/** Every {@link TerrainChannel}, in pane order. The pane's authored region is exactly this long. */
export const TERRAIN_CHANNELS = ["floor", "cover", "sight", "hazard"] as const satisfies readonly TerrainChannel[];

/** One channel's answer for one cell. Always emitted — an at-default channel says so rather than vanishing. */
export type ChannelLine = {
  channel: TerrainChannel;
  /** The channel's name — the question it answers, as a two-word column head. */
  label: string;
  /** The short answer, e.g. `"Hard"`, `"Difficult (costs 2)"`. */
  value: string;
  /** What that answer does to play, or `null` when the answer is the plain default and says it all. */
  detail: string | null;
  /** Whether this channel sits at its open-ground default (the surface may dim it; the row still exists). */
  plain: boolean;
};

/**
 * One of the other three carriers present at this cell — an edge wall on one of its boundaries, a
 * placed object/deployable standing on it, or a live area effect covering it. Presence-driven, unlike
 * the channels: a cell with no walls, objects or clouds simply has none of these, and their absence
 * is the answer.
 */
export type CarrierNote = {
  carrier: "edge" | "object" | "area";
  /** Stable within one description — the surface keys its rows on this. */
  id: string;
  label: string;
  detail: string;
};

/** The armed shot the resolved register is relative to. `weapon` is `null` for an aim that resolves to no weapon profile (no tags ⇒ no exemptions ⇒ every wall stands). */
export type ArmedShot = {
  attacker: Combatant;
  weapon: WeaponProfile | null;
  /** The merged overlay, so allied hard cover (the Goliath's Guardian) reads as the shot folds it — see `armedCellSightlines`. */
  overlay?: LoadedOverlay;
};

/**
 * What this cell does to **one specific armed shot** — the resolved register (D4). Present only while
 * an attack is armed, and it always names what it is relative to.
 */
export type ResolvedShot = {
  /** The header — "Against Kodiak, firing Assault Rifle". Names attacker AND weapon per D5. */
  against: string;
  /** Whether the attacker can target this cell (the tag exemption already applied). */
  visible: boolean;
  /** The cover degree the advisory reports (the tag exemption already applied). */
  cover: Cover;
  /** The tag standing a wall down for this shot, or `null`. */
  exemption: "seeking" | "arcing" | null;
  /** The sentences, in order: line of sight, then cover, then any authored-vs-resolved divergence worth naming. */
  lines: string[];
};

/**
 * What is painted on a cell, by name: the catalogue tile's name (or "Custom terrain" for a one-off
 * cell), and one plain-words sentence per property it carries. The mechanics are resolved through
 * the tile, so the sentences always describe the rule the engine applies (`decisions/0185`).
 */
export type TileReading = {
  name: string;
  /** Whether the cell is a one-off custom cell rather than a catalogue tile. */
  custom: boolean;
  /** One sentence per property, in `terrainTiles.ts`' fixed order. */
  properties: TileProperty[];
};

/** Everything the pane says about one tile. */
export type CellDescription = {
  /** The cell's space key (`"x,y"`) — the same vocabulary every other cell-keyed read uses. */
  key: string;
  cell: SquareCell;
  /** What is painted here, by name, leading the pane — `null` on open ground, which keeps today's four-channel reading alone. */
  tile: TileReading | null;
  /** The authored channels — always {@link TERRAIN_CHANNELS}, always in that order. */
  channels: ChannelLine[];
  /** The other three carriers, when present at this cell. */
  carriers: CarrierNote[];
  /** The attacker-relative register, or `null` when no attack is armed (D6). */
  resolved: ResolvedShot | null;
};

/** The four orthogonal neighbours of a square cell, paired with the compass word for the edge between. */
const NEIGHBOURS = [
  { dir: "North", dx: 0, dy: -1 },
  { dir: "East", dx: 1, dy: 0 },
  { dir: "South", dx: 0, dy: 1 },
  { dir: "West", dx: -1, dy: 0 },
] as const;

/**
 * What a cover degree costs the attacker, as the pane writes it.
 *
 * **Difficulty, never a flat modifier.** Core p.66 gives soft cover +1◆ and hard cover +2◆ on ranged
 * attacks; p.13 defines a point of Difficulty as *subtract 1d6, keep the highest*. Hard cover is
 * therefore the highest of 2d6 — about −4.5 on average — so writing it as "−2" understates it by
 * half and teaches the wrong mechanic besides. The engine agrees: the attack result card reads
 * "+2 Diff (hard cover)" (`packages/domain/src/encounter/steps.ts:186`), and its internal `coverAcc`
 * is a count of DICE, not a modifier. The gloss is deliberately omitted here and carried by the
 * legend instead — the pane is read mid-shot and repeats this fragment on three carriers.
 */
/** A shield dome grants no cover, so the legend's cloud copy would misdescribe it. */
/** The copy for each area that isn't a smoke cloud (`LEGEND_COPY.area`'s default). */
const AREA_DETAIL: Partial<Record<AreaGrant, string>> = {
  outside_attack_difficulty: "A shield dome. A ranged or melee attack on anyone inside it, made from outside it, takes +1 Difficulty.",
  blind_out: "Blinding light. Anyone inside it but the thrower can't draw line of sight out of it. It gives no cover.",
  no_reactions: "Distorted timeflow. Anyone inside it but its maker can't take reactions. It gives no cover.",
  none: "A marked space. Its maker's system acts on whoever starts a turn in it or enters it. It gives no cover.",
};

const COVER_DIFFICULTY = { soft: "+1 Difficulty", hard: "+2 Difficulty" } as const;

/** What a cell's own cover grade does, with the Difficulty gloss (Core p.13) the pane's other cover
 *  fragments leave out. Who gets the cover matches the tile's own property line (`tilePropertyList`):
 *  a target standing ON hard cover gets only soft cover, the engine's occupancy floor in `terrain.ts`. */
export const COVER_DETAIL = {
  hard: "+2 Difficulty on ranged attacks against a target sheltering behind it and adjacent to it — subtract the highest of 2d6. A target standing in it, or further back, gets soft cover instead.",
  soft: "+1 Difficulty on ranged attacks against a target in it or behind it — subtract 1d6.",
} as const;

/** The FLOOR channel — "can I stand there, and what does it cost". Movement flags only; sight is its own channel. */
function floorLine(flags: TerrainProperties): ChannelLine {
  const label = "Floor";
  const cost = flags.moveCost ?? 1;
  if (flags.blocksMovement === true && flags.blocksFlight === true) {
    // Full height (RAW p.63's exception to flight's ignore-obstructions default): the one floor a
    // flyer cannot answer.
    return { channel: "floor", label, value: "Solid, full height", detail: "Nothing crosses it — a flyer is stopped by it too.", plain: false };
  }
  if (flags.blocksMovement === true) {
    return {
      channel: "floor",
      label,
      value: "Impassable",
      detail: "Ground movement can't enter it. A flyer crosses over, but can't end its move here.",
      plain: false,
    };
  }
  if (cost >= 2) {
    // The magnitude is spelled out even though the board draws every cost identically (D10 keeps it
    // that way deliberately) — the pane is exactly where a distinction the tiles don't carry belongs.
    return { channel: "floor", label, value: `Difficult (costs ${cost})`, detail: `Costs ${cost} to enter instead of 1.`, plain: false };
  }
  return { channel: "floor", label, value: "Open", detail: "Costs 1 to enter.", plain: true };
}

/** The COVER channel — "does it protect whoever shelters here". The cell's OWN grade; an object's or a cloud's is its own carrier note. */
function coverLine(flags: TerrainProperties): ChannelLine {
  const label = "Cover";
  const grade = flags.cover ?? "none";
  if (grade === "hard") return { channel: "cover", label, value: "Hard", detail: COVER_DETAIL.hard, plain: false };
  if (grade === "soft") return { channel: "cover", label, value: "Soft", detail: COVER_DETAIL.soft, plain: false };
  return { channel: "cover", label, value: "None", detail: "Nothing here shelters a target standing in it.", plain: true };
}

/** The SIGHT channel — "can I see and shoot through it". */
function sightLine(flags: TerrainProperties): ChannelLine {
  const label = "Sight";
  return flags.blocksSight === true
    ? { channel: "sight", label, value: "Blocked", detail: "Sightlines stop here — nothing sees or shoots through this cell.", plain: false }
    : { channel: "sight", label, value: "Passes", detail: "Sightlines pass through freely.", plain: true };
}

/**
 * The HAZARD channel — "will it hurt me". **Names the damage type in words**, which the board can only
 * gesture at: since Phase 3b the board draws a glyph inside the amber rim for each of the four types
 * Core p.62 names, and a bare rim for anything else it has no mark for (a `heat` cell stored before
 * the authoring palette narrowed, or a CLI-written `variable`). This line is what makes an unmarked
 * rim readable rather than a mystery, so it reports whatever type is STORED — never only the four.
 *
 * **The trigger and the target number are RAW, checked against the book rather than against the
 * code.** Core p.62: a character ending its turn in dangerous terrain *or moving into it for the
 * first time in a round* makes an ENGINEERING check, taking 5 damage on a failure, and needs only one
 * such check per round. Core p.13: "target number" is the book's term (and the GM can't change it from
 * 10), so the copy says that rather than the engine-internal "DC". Our resolver agrees exactly —
 * `DANGEROUS_TERRAIN_DC = 10` with `total >= DC` passing, `DANGEROUS_TERRAIN_DAMAGE = 5`, and a
 * once-per-round resource key (`packages/rules/src/encounter/resolve/dangerousTerrain.ts:20-32`). Say
 * "target number 10", never "beats 10": a 10 passes.
 *
 * **Armor is type-aware, not "as usual" for every type.** Core p.67: AP weapons and burn damage ignore
 * ARMOR altogether, and heat "ignores ARMOR" outright. `packages/rules`' own {@link isPhysical} draws
 * exactly this line (kinetic/energy/explosive vs. burn/heat) for the same reason Exposed's doubling
 * needs it, and `resolveDangerousTerrain.test.ts`'s "hazard type is read from the cell — burn ignores
 * armor, kinetic doesn't" pins the resolver doing the same thing. Reusing it here means this string
 * cannot drift from the resolver's own armor fold the way it did before.
 */
function hazardLine(flags: TerrainProperties): ChannelLine {
  const label = "Hazard";
  if (flags.dangerous !== true) return { channel: "hazard", label, value: "None", detail: null, plain: true };
  const type = flags.hazard ?? DEFAULT_HAZARD_TYPE;
  const armorNote = isPhysical(type) ? "mitigated by armor as usual" : "ignoring armor — this type bypasses it entirely";
  return {
    channel: "hazard",
    label,
    value: `Dangerous (${type})`,
    detail: `Entering it for the first time in a round, or ending a turn in it, forces an ENGINEERING check (target number 10) — 5 ${type} damage on a failure, ${armorNote}. One check per character per round.`,
    plain: false,
  };
}

/** An edge wall's flags as one sentence, in the same consequence register the TERRAIN rail's `title=` strings use. */
function edgeDetail(flags: EdgeTerrain): string {
  const bits: string[] = [];
  if (flags.blocksMovement === true) {
    bits.push(flags.blocksFlight === true ? "Blocks movement, a flyer included" : "Blocks ground movement — flyable");
  }
  bits.push(flags.blocksSight === true ? "blocks sight" : "sight passes");
  // The low wall's cover grade. The board drew this PIXEL-IDENTICALLY at soft and hard until
  // 2026-09-01 (`CAPABILITIES.md`'s own named gap) and the pane was the only surface that reported
  // it; Phase 3b gave the three low walls three stroke patterns, so the two agree now. The pane stays
  // the surface of record for a grade on a FULL wall, which the board deliberately does not draw.
  const cover = flags.cover ?? "none";
  if (cover !== "none") bits.push(`${COVER_DIFFICULTY[cover]} on a ranged shot crossing it (${cover} cover)`);
  const [first, ...rest] = bits;
  return rest.length > 0 ? `${first}, ${rest.join(", ")}.` : `${first}.`;
}

/** The edge carrier: every boundary of this cell that carries any authored wall flag. */
function edgeNotes(state: EncounterState, cell: SquareCell): CarrierNote[] {
  const space = worldFor(state);
  const out: CarrierNote[] = [];
  for (const { dir, dx, dy } of NEIGHBOURS) {
    // `squareCell` rather than an object literal: it carries the `grid` tag the spatial port keys on
    // AND normalizes `-0`, so an edge on the x=0 or y=0 boundary keys identically to the same edge
    // reached from the other side.
    const neighbour = squareCell(cell.x + dx, cell.y + dy);
    const flags = edgeTerrain({ space, edges: state.edges, a: cell, b: neighbour });
    if (!flags) continue;
    // An edge carrying nothing at all can exist in a sparse map only as a stale entry; skip it rather
    // than claim a wall the board draws nothing for.
    if (flags.blocksMovement !== true && flags.blocksSight !== true && (flags.cover ?? "none") === "none") continue;
    out.push({ carrier: "edge", id: `edge:${dir}`, label: `${dir} edge`, detail: edgeDetail(flags) });
  }
  return out;
}

/** An object's spatial contribution as one sentence — the pane's own read of the same `ObjectProfile` the LoS/cover fold consumes. */
function objectDetail(c: Combatant): string {
  const profile = c.base.objectProfile ?? {};
  // A destroyed object, or an open door, is still ON the tile and still worth reporting — the reason
  // it stops mattering is exactly what a GM wondering "why doesn't this block?" needs to read.
  if (hasStatus(c.statuses, "Destroyed")) return "Destroyed — it no longer blocks anything or grants cover.";
  if (isOpenDoor(c)) return "Open — while it stands open it blocks nothing and grants no cover.";
  const bits: string[] = [];
  // Same flyable/full-height split the terrain sentence speaks (`blocksFlight`, D1).
  if (profile.blocksMovement === true) bits.push(profile.blocksFlight === true ? "blocks movement, a flyer included" : "blocks ground movement (flyable)");
  if (profile.blocksSight === true) bits.push("blocks sight");
  const cover = profile.cover ?? "none";
  if (cover !== "none") bits.push(`grants ${cover} cover (${COVER_DIFFICULTY[cover]} on ranged attacks)`);
  if ((profile.moveCost ?? 1) >= 2) bits.push(`costs ${profile.moveCost} to cross`);
  if (bits.length === 0) return "Purely cosmetic — no effect on movement, sight or cover.";
  const sentence = bits.join(", ");
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}. Destroying it removes all of that.`;
}

/** The object carrier: every placed object/deployable whose footprint covers this cell. */
function objectNotes(state: EncounterState, cell: SquareCell): CarrierNote[] {
  const space = worldFor(state);
  const out: CarrierNote[] = [];
  for (const c of Object.values(state.combatants)) {
    // `contributesToTerrain` is the same presence check `objectContributions.ts` filters on, so the
    // pane reports exactly the set of combatants that can reach the LoS/cover fold at all — a mech
    // standing here is a token with its own inspector, not terrain.
    if (!contributesToTerrain(c) || c.pos == null) continue;
    if (!space.footprint(c.pos, c.base.size).contains(cell)) continue;
    out.push({ carrier: "object", id: `object:${c.id}`, label: c.name, detail: objectDetail(c) });
  }
  return out;
}

/** The area carrier: every live standing cloud whose disk covers this cell. */
function areaNotes(state: EncounterState, cell: SquareCell): CarrierNote[] {
  const out: CarrierNote[] = [];
  const space = worldFor(state);
  for (const area of Object.values(state.areaEffects ?? {})) {
    // The cloud stores its TEMPLATE, never per-cell membership (`areaEffects.ts`), so containment is
    // materialized on demand through the same `space.disk` geometry `areaContributions.ts` uses.
    if (!space.disk(area.origin, area.size).contains(cell)) continue;
    const detail = AREA_DETAIL[area.grants] ?? LEGEND_COPY.area.detail;
    out.push({ carrier: "area", id: `area:${area.id}`, label: LEGEND_COPY.area.label, detail });
  }
  return out;
}

/**
 * The resolved register for one armed shot at this cell.
 *
 * **The numbers are `armedCellSightlines`' own**, called with this single cell as its reach list —
 * not a second LoS query written here. That is the same discipline `terrainPalette.ts` enforces for
 * colour: the pane must not be able to disagree with the hatch the board is painting under the
 * cursor. The tag exemption comes from the SAME {@link losExemptionFor} that advisory applies.
 *
 * `null` when the attacker is unplaced (there is no footprint to shoot from, so there is nothing
 * honest to report).
 */
function resolvedShot(args: {
  state: EncounterState;
  cell: SquareCell;
  key: string;
  armed: ArmedShot;
  flags: TerrainProperties;
}): ResolvedShot | null {
  const { state, cell, key, armed, flags } = args;
  const { attacker, weapon } = armed;
  const [sightline] = armedCellSightlines({ state, attacker, reachCells: [{ key, cell }], weapon, ...(armed.overlay !== undefined ? { overlay: armed.overlay } : {}) });
  if (!sightline) return null;
  const exemption = losExemptionFor(weapon);
  const against = weapon ? `Against ${attacker.name}, firing ${weapon.name}` : `Against ${attacker.name}`;
  const lines: string[] = [];

  // Line of sight. Under either tag the honest sentence is about the TAG, not about the geometry —
  // `terrainVisibility` still ran, and reporting its `visible` bit would describe a wall that does
  // not apply to this shot.
  if (exemption === "seeking") {
    lines.push("SEEKING ignores line of sight — this cell can be targeted whether or not anything stands between.");
  } else if (exemption === "arcing") {
    lines.push("ARCING ignores line of sight — the shot lobs over whatever stands between.");
  } else {
    lines.push(sightline.visible ? "In line of sight." : "No line of sight — this shot can't be taken.");
  }

  // Cover. The SEEKING sentence is the whole reason this register exists: the board suppresses the
  // hatch for a Seeking shot, so an unhatched cell means either "no cover here" OR "cover suppressed
  // by the tag" and nothing on screen tells the two apart. Reporting a bare `none` here would repeat
  // that ambiguity in words.
  if (exemption === "seeking") {
    lines.push("Cover is suppressed by SEEKING — none applies to this shot, whatever this cell would otherwise grant.");
  } else {
    const base =
      sightline.cover === "none"
        ? "No cover against this attacker."
        : `${sightline.cover === "hard" ? "Hard" : "Soft"} cover — ${COVER_DIFFICULTY[sightline.cover]} on this attacker's ranged shot at a target here.`;
    // ARCING sets aside line of sight and NOTHING else: its cover degree is the same number the
    // to-hit fold will use, so the pane says so rather than leaving the GM to assume the tag covers both.
    lines.push(exemption === "arcing" ? `${base} ARCING sets aside line of sight, not cover — this degree still applies.` : base);
  }

  // The two places authored and resolved genuinely diverge, named rather than left as a
  // contradiction between the two regions the reader can see at once.
  const authored = flags.cover ?? "none";
  if (exemption === null) {
    if (authored === "hard" && sightline.cover === "soft") {
      lines.push("A target standing in hard cover gets soft cover from it, never hard — hard cover has to be between the shot and the target (Core p.66).");
    } else if (authored === "none" && sightline.cover !== "none") {
      lines.push("That cover comes from between the attacker and this cell, not from the cell itself.");
    }
  }

  return { against, visible: sightline.visible, cover: sightline.cover, exemption, lines };
}

/** Named arguments for {@link describeCell}. */
export type DescribeCellArgs = {
  state: EncounterState;
  cell: SquareCell;
  /** The armed shot to resolve against, when an attack is armed. Omitted/`null` ⇒ no resolved register (D6). */
  armed?: ArmedShot | null;
};

/**
 * Everything the hovered-tile pane says about `cell` — the pure fold the pane renders.
 *
 * Exported and DOM-free for the same reason `legendFor`/`mapImageLegend` are: the wording is part of
 * what the board *says*, not merely how it looks, so the SEEKING and ARCING phrasing can be pinned
 * without mounting a component or decoding a canvas. This is the highest architectural boundary that
 * observes the behaviour, and it is deliberately not the panel component.
 *
 * **Square-fenced.** Takes a {@link SquareCell} because the pane is a surface of the square combat
 * board (the same fence `combatCells.ts` holds), and its edge walk enumerates the four orthogonal
 * neighbours directly. Everything else keys through the encounter's own space (`worldFor`), so
 * nothing here hardcodes a grid beyond that walk.
 */
export function describeCell(args: DescribeCellArgs): CellDescription {
  const { state, cell, armed } = args;
  const key = worldFor(state).key(cell);
  const flags = propertiesAtKey(state.terrain, key) ?? {};
  const entry = state.terrain[key];
  const name = cellTileName(entry);
  return {
    key,
    cell,
    tile: name === null || entry === undefined ? null : { name, custom: entry.tile === CUSTOM_TILE, properties: tilePropertyList(flags) },
    channels: [floorLine(flags), coverLine(flags), sightLine(flags), hazardLine(flags)],
    carriers: [...edgeNotes(state, cell), ...objectNotes(state, cell), ...areaNotes(state, cell)],
    resolved: armed ? resolvedShot({ state, cell, key, armed, flags }) : null,
  };
}
